import React, { memo, useEffect, useMemo, useRef } from 'react';
import katex from 'katex';
import renderMathInElement from 'katex/dist/contrib/auto-render';
import 'katex/dist/katex.min.css';
import './katex-text.scss';

const MATH_PATTERN = /\$\$([^$]+)\$\$/g;

export function splitKatex(text) {
  if (typeof text !== 'string' || text === '') return [];
  const pattern = new RegExp(MATH_PATTERN);
  const parts = [];
  let cursor = 0;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) parts.push({ text: text.slice(cursor, match.index) });
    parts.push({ math: match[1], raw: match[0] });
    cursor = pattern.lastIndex;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor) });
  return parts;
}

// Keep a formula whole when an existing character limit truncates a title.
export function truncateKatex(text, max, suffix = '..') {
  if (typeof text !== 'string' || text.length <= max) return text;
  let result = '';
  for (const part of splitKatex(text)) {
    const value = part.raw || part.text;
    const remaining = max - result.length;
    if (value.length <= remaining) {
      result += value;
    } else {
      if (part.math == null) result += value.slice(0, Math.max(0, remaining));
      break;
    }
  }
  return result + suffix;
}

const MathText = memo(({ math }) => {
  const markup = useMemo(
    () => katex.renderToString(math, { throwOnError: false, displayMode: false }),
    [math]
  );
  return <span className="queture-math" dangerouslySetInnerHTML={{ __html: markup }} />;
});

// Existing formatting/highlighting may transform text, but never a formula token.
const KatexText = ({ text, renderText, ...spanProps }) => {
  const parts = useMemo(() => splitKatex(text), [text]);
  if (typeof text !== 'string') return text == null ? null : text;
  const renderPlainText = (part, index) => {
    let rendered = renderText ? renderText(part.text) : part.text;
    if (renderText && typeof rendered === 'string') {
      // Trim-based legacy formatters must not join the words around a formula.
      if (index > 0 && parts[index - 1].math != null) {
        rendered = part.text.match(/^\s*/)[0] + rendered.replace(/^\s*/, '');
      }
      if (index + 1 < parts.length && parts[index + 1].math != null) {
        rendered = rendered.replace(/\s*$/, '') + part.text.match(/\s*$/)[0];
      }
    }
    return rendered;
  };
  const content = parts.map((part, index) =>
    part.math != null ? (
      <MathText key={index} math={part.math} />
    ) : (
      <React.Fragment key={index}>
        {renderPlainText(part, index)}
      </React.Fragment>
    )
  );
  return Object.keys(spanProps).length ? <span {...spanProps}>{content}</span> : <>{content}</>;
};

// Prologues already contain HTML. Render formulas only in their text nodes.
export const KatexHtml = ({ html, as: Tag = 'span', ...props }) => {
  const ref = useRef(null);
  const preparedHtml = useMemo(() => (html || '').replace(new RegExp(MATH_PATTERN), (formula) =>
    formula
      .replace(/<br\s*\/?\s*>/gi, '\n')
      .replace(/&(?!(?:#\d+|#x[\da-f]+|[a-z][\da-z]+);)/gi, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  ), [html]);
  useEffect(() => {
    if (!ref.current) return;
    renderMathInElement(ref.current, {
      delimiters: [{ left: '$$', right: '$$', display: false }],
      throwOnError: false,
    });
  }, [preparedHtml]);
  return <Tag {...props} ref={ref} dangerouslySetInnerHTML={{ __html: preparedHtml }} />;
};

export default KatexText;
